import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faListUl } from '@fortawesome/pro-regular-svg-icons';
import { ChecklistMarker } from '../ChecklistMarker';

// Numbers run down a column before starting the next one, as in the design.
const QUESTIONS_PER_COLUMN = 25;

/**
 * Splits the questions into the columns the expanded list shows them in.
 * @param {Array<Object>} results Output of gradeQuiz, one entry per question
 * @returns {Array<Array<Object>>} Columns of { result, index } entries
 */
const toColumns = (results) => {
  const numbered = results.map((result, index) => ({ result, index }));
  const columns = [];

  for (let start = 0; start < numbered.length; start += QUESTIONS_PER_COLUMN) {
    columns.push(numbered.slice(start, start + QUESTIONS_PER_COLUMN));
  }

  return columns;
};

/**
 * The question rail beside the quiz. It collapses to a single icon and expands
 * into a list of numbers in columns of 25, each marked with how that question
 * stands: right or wrong once graded, answered or not while being taken.
 *
 * Both pages use it so the two rails stay the same shape; only the mark differs.
 *
 * Props:
 *   id          (string)   - ID prefix for generated element IDs.
 *   results     (array)    - Output of gradeQuiz, one entry per question.
 *   isGraded    (bool)     - Whether the quiz has been submitted.
 *   activeIndex (number)   - The question currently in view.
 *   isOpen      (bool)     - Whether the list is expanded.
 *   enabledIndexes (Set)   - Optional; the questions that can be jumped to. The
 *                            rest are listed but unavailable, which is how the
 *                            rail follows the results tabs. Omit to enable all.
 *   onToggle    (function) - Called with the next open state.
 *   onSelect    (function) - Called with the index to jump to.
 */
export const QuestionChecklist = ({
  id,
  results,
  isGraded,
  activeIndex,
  isOpen,
  enabledIndexes,
  onToggle,
  onSelect,
}) => {
  if (!isOpen) {
    return (
      <Box className="questionChecklist collapsed" id={`${id}__checklist`}>
        <IconButton
          id={`${id}__button_openChecklist`}
          className="checklistToggle"
          onClick={() => onToggle(true)}
          aria-label="Show question list"
          aria-expanded={false}
        >
          <FontAwesomeIcon icon={faListUl} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      className="questionChecklist expanded"
      id={`${id}__checklist`}
      component="nav"
      aria-label="Quiz questions"
    >
      <Box className="checklistHeader">
        <FontAwesomeIcon icon={faListUl} className="checklistIcon" aria-hidden />
        <IconButton
          id={`${id}__button_closeChecklist`}
          className="checklistToggle"
          onClick={() => onToggle(false)}
          aria-label="Hide question list"
          aria-expanded
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </IconButton>
      </Box>

      <Box className="checklistColumns">
        {toColumns(results).map((column, columnIndex) => (
          <Box className="checklistColumn" key={columnIndex}>
            {column.map(({ result, index }) => {
              // Listed either way, so the numbering stays the quiz's own, but
              // only jumpable while the tab on screen includes it.
              const isDisabled = !!enabledIndexes && !enabledIndexes.has(index);

              return (
                <Box
                  key={index}
                  className={`checklistItem ${index === activeIndex ? 'active' : ''} ${
                    isGraded ? (result.isCorrect ? 'correct' : 'incorrect') : ''
                  } ${isDisabled ? 'disabled' : ''}`}
                  id={`${id}__checklistItem_${index + 1}`}
                  role="button"
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled || undefined}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  /* The mark is decorative, so the state it conveys goes here. */
                  aria-label={`Go to question ${index + 1}, ${
                    isGraded
                      ? result.isCorrect
                        ? 'correct'
                        : 'incorrect'
                      : result.isAnswered
                        ? 'answered'
                        : 'not answered'
                  }`}
                  onClick={() => !isDisabled && onSelect(index)}
                  onKeyDown={(e) =>
                    !isDisabled && (e.key === 'Enter' || e.key === ' ') && onSelect(index)
                  }
                >
                  <ChecklistMarker
                    isGraded={isGraded}
                    isCorrect={result.isCorrect}
                    isAnswered={result.isAnswered}
                  />
                  <Typography className="checklistNumber">{index + 1}</Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
